import {
  addDoc,
  collection,
  serverTimestamp,
  updateDoc,
  doc,
  query,
  orderBy,
  limit,
  onSnapshot,
  getDoc,
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { firestore, storage } from '../config/firebase';

/**
 * @typedef {'improvement'|'bug'|'feature'} FeedbackCategory
 */

/**
 * @param {{
 *  authorUid: string,
 *  authorDisplayName?: string,
 *  authorEmail?: string,
 *  category: FeedbackCategory,
 *  message: string,
 *  context?: Record<string, any>,
 *  includeConsole?: boolean,
 *  consoleLogTail?: any[],
 *  screenshotBlob?: Blob | null,
 * }} params
 */
export async function submitFeedback({
  authorUid,
  authorDisplayName = '',
  authorEmail = '',
  category,
  message,
  context = {},
  includeConsole = false,
  consoleLogTail = [],
  screenshotBlob = null,
}) {
  if (!authorUid) throw new Error('Missing authorUid');
  const trimmed = (message || '').trim();
  if (!trimmed) throw new Error('Empty message');

  const payload = {
    authorUid,
    authorDisplayName: String(authorDisplayName || '').trim(),
    authorEmail: String(authorEmail || '').trim(),
    category,
    message: trimmed,
    context: context && typeof context === 'object' ? context : {},
    includeConsole: !!includeConsole,
    consoleLogTail: includeConsole ? (Array.isArray(consoleLogTail) ? consoleLogTail : []) : [],
    createdAt: serverTimestamp(),
    status: 'new',
    triagedAt: null,
  };

  const docRef = await addDoc(collection(firestore, 'feedback'), payload);

  if (screenshotBlob) {
    const path = `feedback/${authorUid}/${docRef.id}.png`;
    const storageRef = ref(storage, path);
    await uploadBytes(storageRef, screenshotBlob, { contentType: 'image/png' });
    const downloadUrl = await getDownloadURL(storageRef);
    await updateDoc(doc(firestore, 'feedback', docRef.id), {
      screenshotStoragePath: path,
      screenshotDownloadUrl: downloadUrl,
    });
  }

  return { id: docRef.id };
}

export function subscribeRecentFeedback({ pageSize = 200 } = {}, onRows, onError) {
  const q = query(collection(firestore, 'feedback'), orderBy('createdAt', 'desc'), limit(pageSize));
  return onSnapshot(
    q,
    (snap) => {
      const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      onRows?.(rows);
    },
    (err) => onError?.(err),
  );
}

export async function markFeedbackTriaged(feedbackId, triaged = true) {
  if (!feedbackId) return;
  await setFeedbackStatus(feedbackId, triaged ? 'triaged' : 'new');
}

export async function setFeedbackStatus(feedbackId, status = 'new') {
  if (!feedbackId) return;
  const normalized = status === 'archived' ? 'archived' : status === 'triaged' ? 'triaged' : 'new';
  await updateDoc(doc(firestore, 'feedback', feedbackId), {
    status: normalized,
    triagedAt: normalized !== 'new' ? serverTimestamp() : null,
    archivedAt: normalized === 'archived' ? serverTimestamp() : null,
  });
}

export async function getFeedbackAuthorInfo(uid) {
  const key = String(uid || '').trim();
  if (!key) return null;
  try {
    const snap = await getDoc(doc(firestore, 'users', key));
    if (!snap.exists()) return null;
    const d = snap.data() || {};
    return {
      uid: key,
      displayName: String(d.displayName || '').trim(),
      email: String(d.email || '').trim(),
    };
  } catch (e) {
    // Some environments may block admin reads on users/{uid}; fallback to stored author fields.
    console.warn('getFeedbackAuthorInfo failed:', e?.message || e);
    return null;
  }
}

